import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtValue } from "@/lib/format";
import { DeltaBadge, EmptyState, ErrorState, LoadingState, PageHeader, Panel, SourceBadge, StatusPill } from "@/components/dash/ui";

const CAT_LABEL: Record<string, string> = {
  revenue: "Revenue", customer: "Customer", operations: "Operations",
  support: "Support", stock: "Stock", connector: "Connector",
};

export default function DecisionCentrePage() {
  const pr = useQuery({ queryKey: ["decisions-priorities"], queryFn: dashApi.decisionsPriorities });
  const ch = useQuery({ queryKey: ["decisions-changes"], queryFn: dashApi.decisionsChanges });
  const rk = useQuery({ queryKey: ["decisions-risks"], queryFn: dashApi.decisionsRisks });
  const counts = pr.data?.counts;
  const topChanges = (ch.data?.metrics ?? []).filter((m) => m.change_pct !== null).slice(0, 6);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Executive Decision Centre"
        description="What needs attention today, what changed since yesterday, and what decisions are required — across every BGR system. Read-only intelligence; tracked actions are internal to IRONDash."
        right={counts && (
          <div className="flex gap-2">
            {counts.critical > 0 && <StatusPill status="critical" label={`${counts.critical} critical`} />}
            {counts.warning > 0 && <StatusPill status="warning" label={`${counts.warning} warning`} />}
          </div>
        )}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel
          title="Top priorities"
          className="lg:col-span-2"
          right={<Link to="/decisions/priorities" className="text-xs font-medium text-brand-300 hover:text-brand-200">All priorities →</Link>}
        >
          {pr.isLoading ? <LoadingState /> : pr.isError ? <ErrorState error={pr.error} /> : !pr.data?.priorities.length ? (
            <EmptyState compact>Nothing needs attention right now.</EmptyState>
          ) : (
            <ul className="divide-y divide-ink-500/40">
              {pr.data.priorities.slice(0, 6).map((p) => (
                <li key={p.key} className="flex items-center gap-3 py-2">
                  <StatusPill status={p.severity} />
                  <span className="flex-1 truncate text-sm text-slate-200">{p.title}</span>
                  {p.source && <SourceBadge code={p.source} />}
                  {p.action && <span className="text-[11px] text-emerald-300">tracked</span>}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Risk snapshot"
          right={<Link to="/decisions/risks" className="text-xs font-medium text-brand-300 hover:text-brand-200">Register →</Link>}
        >
          {rk.isLoading ? <LoadingState /> : !rk.data ? <EmptyState compact>No data.</EmptyState> : (
            <ul className="space-y-1.5">
              {rk.data.categories.filter((c) => c.count > 0).map((c) => (
                <li key={c.category} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">{CAT_LABEL[c.category] ?? c.label}</span>
                  <span className="flex items-center gap-2">
                    {c.max_severity && <StatusPill status={c.max_severity} />}
                    <span className="tabular-nums text-slate-400">{c.count}</span>
                  </span>
                </li>
              ))}
              {!rk.data.categories.some((c) => c.count > 0) && <EmptyState compact>No open risks.</EmptyState>}
            </ul>
          )}
        </Panel>
      </div>

      <Panel
        title="What changed since yesterday"
        right={<Link to="/decisions/changes" className="text-xs font-medium text-brand-300 hover:text-brand-200">Detail →</Link>}
      >
        {ch.isLoading ? <LoadingState /> : ch.isError ? <ErrorState error={ch.error} /> : !ch.data?.have_history ? (
          <EmptyState compact>No warehouse history yet — day-over-day deltas appear once daily rollups accrue.</EmptyState>
        ) : !topChanges.length ? (
          <EmptyState compact>No material day-over-day changes.</EmptyState>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {topChanges.map((m) => (
              <div key={m.kpi} className="rounded-lg border border-ink-500/50 bg-ink-700/40 px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">{m.label}</p>
                <div className="mt-0.5 flex items-baseline justify-between">
                  <span className="text-lg font-semibold tabular-nums text-white">{m.current === null ? "—" : fmtValue(m.current, m.unit)}</span>
                  <DeltaBadge pct={m.change_pct} invert={m.kpi === "repairs.overdue" || m.kpi === "stock.low_stock"} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
